import { useMemo, useState } from 'react'
import { CalendarIcon, PlusIcon, XIcon } from './Icons'
import Modal from './Modal'

function toMonthStart(value) {
  return /^\d{4}-\d{2}$/.test(value) ? `${value}-01` : ''
}

function formatMonthStart(value) {
  if (!value) return ''
  const [year, month] = value.slice(0, 7).split('-').map(Number)
  if (!year || !month) return value
  return new Intl.DateTimeFormat('en', { month: 'long', year: 'numeric' }).format(
    new Date(year, month - 1, 1),
  )
}

function shiftMonthStart(value, amount) {
  const [year, month] = value.slice(0, 7).split('-').map(Number)
  const shifted = new Date(year, month - 1 + amount, 1)
  return `${shifted.getFullYear()}-${String(shifted.getMonth() + 1).padStart(2, '0')}-01`
}

export default function MonthSelectorModal({ currentMonth, selectedMonths, onClose, onApply }) {
  const [months, setMonths] = useState(() => [...new Set(selectedMonths)].sort())
  const [monthInput, setMonthInput] = useState(currentMonth.slice(0, 7))
  const [error, setError] = useState('')

  const summary = useMemo(
    () => `${months.length} month${months.length === 1 ? '' : 's'} selected`,
    [months.length],
  )

  function addMonth() {
    const value = toMonthStart(monthInput)
    setError('')
    if (!value) {
      setError('Choose a valid month.')
      return
    }
    setMonths((current) => [...new Set([...current, value])].sort())
  }

  function removeMonth(value) {
    if (months.length === 1) {
      setError('Keep at least one reporting month selected.')
      return
    }
    setError('')
    setMonths((current) => current.filter((month) => month !== value))
  }

  function chooseRecent(count) {
    const values = Array.from({ length: count }, (_, index) =>
      shiftMonthStart(currentMonth, -(count - 1 - index)),
    )
    setMonths(values)
    setError('')
  }

  function apply() {
    if (!months.length) {
      setError('Choose at least one reporting month.')
      return
    }
    onApply(months)
  }

  return (
    <Modal
      className="month-selector-modal"
      onClose={onClose}
      subtitle="Choose one or more months to show in Resource management."
      title="Select reporting months"
    >
      <div className="modal-body month-selector-body">
        <div className="month-selector-summary">
          <span className="state-icon state-icon--soft"><CalendarIcon size={20} /></span>
          <div>
            <strong>{summary}</strong>
            <small>Each resource will have one row for every selected month.</small>
          </div>
        </div>

        <div className="month-quick-actions" aria-label="Quick month selections">
          <button className="button button--secondary button--compact" onClick={() => setMonths([currentMonth])} type="button">
            Current month
          </button>
          <button className="button button--secondary button--compact" onClick={() => chooseRecent(3)} type="button">
            Last 3 months
          </button>
          <button className="button button--secondary button--compact" onClick={() => chooseRecent(6)} type="button">
            Last 6 months
          </button>
        </div>

        <div className="month-assignment month-selector-assignment">
          <div className="month-assignment-heading">
            <strong>Selected months</strong>
            <small>Add any reporting months required for resource comparison and CSV export.</small>
          </div>

          <div className="month-chip-list">
            {months.map((value) => (
              <span className={`month-chip ${value === currentMonth ? 'month-chip--current' : ''}`} key={value}>
                {formatMonthStart(value)}
                {value === currentMonth ? <small>Displayed</small> : null}
                <button
                  aria-label={`Remove ${formatMonthStart(value)}`}
                  onClick={() => removeMonth(value)}
                  type="button"
                >
                  <XIcon size={13} />
                </button>
              </span>
            ))}
          </div>

          <div className="month-add-row">
            <input
              aria-label="Add reporting month"
              onChange={(event) => setMonthInput(event.target.value)}
              type="month"
              value={monthInput}
            />
            <button className="button button--secondary button--compact" onClick={addMonth} type="button">
              <PlusIcon size={15} />
              Add month
            </button>
          </div>
        </div>

        {error ? <p className="form-error">{error}</p> : null}
      </div>

      <footer className="modal-footer">
        <button className="button button--secondary" onClick={onClose} type="button">
          Cancel
        </button>
        <button className="button button--primary" onClick={apply} type="button">
          Show selected months
        </button>
      </footer>
    </Modal>
  )
}
